/**
 * Blog Service for LC Performance
 * 
 * Public functions for fetching published blog posts.
 * No admin verification required - these are public endpoints.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Get all published blogs with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.category - Filter by category
 * @param {string} filters.tag - Filter by tag
 * @param {string} filters.search - Search query (searches title, content, excerpt)
 * @param {number} filters.limit - Number of results (default: 20)
 * @param {number} filters.offset - Pagination offset (default: 0)
 * @param {string} filters.sortBy - Sort field (default: 'published_at')
 * @param {string} filters.order - Sort order 'asc' or 'desc' (default: 'desc')
 * @returns {Promise<{success: boolean, data?: Array, error?: string, count?: number}>}
 */
export async function getAllBlogs(filters = {}) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  try {
    const {
      category,
      tag,
      search,
      limit = 20,
      offset = 0,
      sortBy = 'published_at',
      order = 'desc',
    } = filters;

    // Build query - only published blogs
    let query = supabase
      .from('blogs')
      .select('*', { count: 'exact' })
      .eq('is_published', true)
      .not('published_at', 'is', null);

    // Apply filters
    if (category) {
      query = query.eq('category', category);
    }

    if (tag) {
      query = query.contains('tags', [tag]);
    }

    if (search) {
      // Full-text search on title, excerpt, and seo_content
      query = query.or(
        `title.ilike.%${search}%,excerpt.ilike.%${search}%,seo_content.ilike.%${search}%`
      );
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: order === 'asc' });

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      success: true,
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error fetching blogs:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch blogs',
      data: [],
      count: 0,
    };
  }
}

/**
 * Get a single published blog by slug
 * @param {string} slug - Blog slug
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function getBlogBySlug(slug) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured' };
  }

  if (!slug) {
    return { success: false, error: 'Slug is required' };
  }

  try {
    const { data, error } = await supabase
      .from('blogs')
      .select(`
        *,
        user_profiles:author_id (
          id,
          full_name,
          email
        )
      `)
      .eq('slug', slug)
      .eq('is_published', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return { success: false, error: 'Blog post not found' };
      }
      throw error;
    }

    // Increment view count (fire and forget)
    if (data && data.id) {
      supabase
        .from('blogs')
        .update({ view_count: (data.view_count || 0) + 1 })
        .eq('id', data.id)
        .then(() => {
          // Silently update view count
        })
        .catch(err => {
          console.warn('Failed to increment view count:', err);
        });
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching blog by slug:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch blog post',
    };
  }
}

/**
 * Get all unique blog categories
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getBlogCategories() {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('blogs')
      .select('category')
      .eq('is_published', true)
      .not('category', 'is', null);

    if (error) throw error;

    // Get unique categories with counts
    const categoryMap = {};
    (data || []).forEach(blog => {
      if (blog.category) {
        categoryMap[blog.category] = (categoryMap[blog.category] || 0) + 1;
      }
    });

    const categories = Object.entries(categoryMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    return { success: true, data: categories };
  } catch (error) {
    console.error('Error fetching blog categories:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch categories',
      data: [],
    };
  }
}

/**
 * Search blogs using full-text search
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {number} options.limit - Number of results (default: 20)
 * @param {number} options.offset - Pagination offset (default: 0)
 * @returns {Promise<{success: boolean, data?: Array, error?: string, count?: number}>}
 */
export async function searchBlogs(query, options = {}) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  if (!query || query.trim().length === 0) {
    return getAllBlogs(options);
  }

  try {
    const { limit = 20, offset = 0 } = options;

    // Use full-text search on seo_content if available, otherwise fallback to ILIKE
    const { data, error, count } = await supabase
      .from('blogs')
      .select('*', { count: 'exact' })
      .eq('is_published', true)
      .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,seo_content.ilike.%${query}%`)
      .order('published_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return {
      success: true,
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error searching blogs:', error);
    return {
      success: false,
      error: error.message || 'Failed to search blogs',
      data: [],
      count: 0,
    };
  }
}

/**
 * Get related blogs (same category, excluding current blog)
 * @param {string} currentBlogId - Current blog ID to exclude
 * @param {string} category - Category to match
 * @param {number} limit - Number of related blogs (default: 3)
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getRelatedBlogs(currentBlogId, category, limit = 3) {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase not configured', data: [] };
  }

  try {
    const { data, error } = await supabase
      .from('blogs')
      .select('*')
      .eq('is_published', true)
      .eq('category', category)
      .neq('id', currentBlogId)
      .order('published_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching related blogs:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch related blogs',
      data: [],
    };
  }
}
